import { Box, Flex, Text as ChakraText, Center, Spinner } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { createBrowserHistory } from 'history'
import { ReactElement, useMemo } from 'react'
import { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ListItem, UnorderedList } from '@chakra-ui/layout'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'
import { CachePolicies, Provider as FetchProvider } from 'use-http'
import _ from 'lodash'
import { GlobalStyle } from '../styles/global-styles'
import { PermissionsEnum } from '../utils'
import { removeTrailingSlash } from '../utils/urlHelpers'
import { IDRegion } from './components/GetStarted'
import PermissionGuard from './components/PermissionGuard'
import SessionCheck from './components/SessionCheck'
import { NavBarLayout } from './layouts'
import {
  AnalyticsRouter,
  BillingRouter,
  AWSManagedPage,
  DeveloperPage,
  ExternalStorageRouter,
  HomePage,
  MediaLibraryRouter,
  MediaCollectionRouter,
  NotFoundPage,
  NotificationPage,
  PerformanceRouter,
  PurgeCachePage,
  ReferralsPage,
  SettingsRouter,
  SupportRouter,
  UrlEndpointsRouter,
  UserManagementRouter,
  UserProfilePage,
  AuditLogsPage,
  MediaAuditLogsPage,
  PlanChangePage,
  InvoiceRouter,
} from './pages'
import { AgencyAnalyticsPage, AgencyManageAccountsRouter } from './pages/Agency'
import ErrorPage from './pages/Error/ErrorPage'
import { CSRFContext, NavBarContext, UserContext } from './providers'
import { RouterBackStackProvider } from './providers/RouterBackStackProvider'
import { CustomMetadataProvider } from './providers/CustomMetadataProvider'
import { PublicLinksInfoContext } from './providers/PublicLinksInfoProvider'
import { useCookies } from 'react-cookie'
import { DiscountedPricingClosableInfoBar } from './components/Billing/DiscountedPricing'
import { getCheckListItems, useProductOnboardingMetrics } from './layouts/Home/Checklist'
import { LabsRouter } from './pages/Labs/Router'
import { useMLWidgetContext } from './contexts/MLWidgetContext'
import type { MLWidgetSettings } from './interfaces'

// this is needed for global variable for HeadwayApp
// export interface AppWindow extends Window {
//   Headway: any
//   HeadwayConfig: any
// }

// declare let window: AppWindow

const DefaultLandingPage = (): ReactElement | null => {
  const { user } = useContext(UserContext)
  const checklistItems = getCheckListItems({ user });
  const { productAdoptionMetrics, loading } = useProductOnboardingMetrics();
  const allItemsComplete = checklistItems.every(item => {
    return productAdoptionMetrics[item.metricName]
  });
  const DAM_ONLY_PLAN = user?.pricingPlan.planType === "DAM_ONLY"

  if (loading || !user) return null;

  if (!allItemsComplete) {
    return <HomePage />
  }

  if (DAM_ONLY_PLAN && user.permissions.includes('RESTRICTED_SELF_MEDIA_LIBRARY_VIEW')) {
    return <Redirect to="/media-library" />
  } else if (user.permissions.includes('SELF_INTEGRATION_VIEW')) {
    return <Redirect to="/external-storage" />
  }

  return <Redirect to="/profile" />
}

const IKDashboardApp = (): ReactElement | null => {
  const { i18n } = useTranslation()
  const { token, loading } = useContext(CSRFContext)
  const { user, loading: userLoading } = useContext(UserContext)
  const [cookies, setCookie] = useCookies(['validateLoginRequestedFromApp'])

  const { isMediaLibraryWidget = false } = window
  const {
    isPublicLink,
    assetType,
    assetId,
    assetBase64Path,
    urlPrefix,
  } = useContext(PublicLinksInfoContext)

  const routingBaseName = (
    isMediaLibraryWidget ?
      '/media-library-widget/' : (
        isPublicLink ?
          urlPrefix :
          '/dashboard/'
      )
  )

  const history = createBrowserHistory({
    basename: routingBaseName
  })

  const { updateSettings } = useMLWidgetContext()

  useEffect(() => {
    Sentry.init({
      dsn: 'https://8640a39841f24f8b91189ee00204f193@o1277188.ingest.sentry.io/6474587',
      integrations: [
        new Integrations.BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
        }),
      ],
      attachStacktrace: true,
      environment: window.env ?? 'dev',
      enabled: (window.env ?? 'dev') !== 'dev',
      tracesSampleRate: 1.0,
      autoSessionTracking: false,
      release: window.SENTRY_RELEASE,
    })
    // window.Headway.init(window.HeadwayConfig)

    const mlWidgetMessageListener = (event: MessageEvent): void => {
      try {
        const newSettings: MLWidgetSettings = {}

        if (window.widgetHost) {
          const widgetHostOrigin = new URL(window.widgetHost).origin

          if (
            !_.isEqual(event.origin.toString(), widgetHostOrigin.toString())
          ) {
            return
          }

          const data = JSON.parse(event.data)

          if (data) {
            if (data?.mlSettings?.multiple === false) {
              newSettings.multiple = false
            } else {
              newSettings.multiple = true
              if (
                data?.mlSettings?.maxFiles &&
                _.isNumber(data.mlSettings.maxFiles) &&
                data.mlSettings.maxFiles > 0
              ) {
                newSettings.maxFiles = data.mlSettings.maxFiles
              }
            }

            if (data?.mlSettings?.initialView) {
              newSettings.initialView = data.mlSettings.initialView
            }

            if (data?.mlSettings?.toolbar) {
              newSettings.toolbar = data.mlSettings.toolbar
            }

            updateSettings(newSettings)
          }
        }
      } catch (e) {
        console.error('Error occurred in MediaLibraryWidget postMessage', e)
      }
    }

    window.addEventListener('message', mlWidgetMessageListener, false)

    return () => {
      window.removeEventListener('message', mlWidgetMessageListener)
    }
  }, [])

  const exceededArray = useMemo(() => {
    if (isPublicLink) {
      return []
    }

    const exceededArray: string[] = [];

    if (!user?.accountEnabled) exceededArray.push("The account is disabled. Please get in touch with support at support@imagekit.io.");
    if (user?.bandwidthExceeded) exceededArray.push("Bandwidth usage exceeded free plan limits.");
    if (user?.storageExceeded) exceededArray.push("Storage usage exceeded free plan limits.");
    if (user?.limitExceeded?.extensions) {
      if (user?.pricingPlan?.isPaidPlan) {
        if (user?.extensionMaximumOverage && user?.extensionMaximumOverage > 0) {
          exceededArray.push(`Extensions usage exceeded plan limits. Maximum ${user?.extensionMaximumOverage} extensions units can be consumed in a month in the current plan.`);
        } else {
          exceededArray.push("Extensions usage exceeded plan limits.");
        }
      } else {
        exceededArray.push("Extensions usage exceeded free plan limits.");
      }
    }
    if (user?.limitExceeded?.transformations?.video) exceededArray.push("Video transformation usage exceeded free plan limits.");

    return exceededArray
  }, [user, isPublicLink])

  // if found URL is "/", then redirect to this URL
  const routeSuffixToRedirectToForBaseUrl = useMemo<string>(() => {
    let routeSuffix = "/media-library"

    if (isPublicLink) {
      if (
        assetType === "file" &&
        assetId
      ) {
        routeSuffix = `/media-library/detail/${assetId}`
      } else if (
        assetType === "folder" &&
        assetBase64Path
      ) {
        routeSuffix = `/media-library/${assetBase64Path}`
      } else if (
        assetType === "media-collection" &&
        assetId
      ) {
        routeSuffix = `/media-collections/${assetId}`
      }
    }

    return routeSuffix
  }, [
    isPublicLink,
  ])

  if (
    !isPublicLink &&
    (
      userLoading ||
      !(user && user.clientNumber && user.imagekitId && user.userEmail)
    )
  ) {
    return (
      <Center width="full" height="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  if (loading) {
    return null
  }

  return (
    !cookies.validateLoginRequestedFromApp ?
      <Sentry.ErrorBoundary fallback={ErrorPage} showDialog>
        <FetchProvider
          options={{
            cacheLife: 60000,
            cachePolicy: CachePolicies.NO_CACHE,
            interceptors: {
              request: ({ options, url }) => {
                if (options.headers) {
                  options.headers['Content-Type'] = 'application/json'
                } else {
                  options.headers = { 'Content-Type': 'application/json' }
                }

                if (
                  url &&
                  window.location.host === new URL(url).host &&
                  options.method &&
                  !['GET', 'HEAD', 'OPTIONS'].includes(options.method)
                ) {
                  options.headers['csrf-token'] = token
                }

                return options
              },
              response: async ({ response }) => {
                // it would mean that the public link password has changed, so make the user enter it again by reloading the page
                const body = response.data
                if (
                  isPublicLink &&
                  response.status === 401 &&
                  body?.reason === 'INVALID_PASSWORD'
                ) {
                  window.location.reload()
                  /**
                   * A promise that never resolves or rejects, so that whoever invoked the fetch takes no further action...
                   * ...and the whole page just reloads because of the `window.location.reload()` above.
                   * This is a hack to avoid handling an explicit error at dozens of places where requests are made
                  */
                  return new Promise(() => { })
                }
                return response
              },
            },
          }}
        >
          <Router
            basename={routingBaseName}
          >
            <Helmet
              titleTemplate="%s - ImageKit Dashboard"
              defaultTitle="ImageKit Dashboard"
              htmlAttributes={{ lang: i18n.language }}
            >
              <meta name="description" content="ImageKit Dashboard" />
            </Helmet>

            {
              !isPublicLink ?
                <SessionCheck /> :
                null
            }

            <RouterBackStackProvider>
              <>
                {!isPublicLink && !isMediaLibraryWidget && window.user?.isSimulation ? (
                  <Box
                    width="full"
                    bgColor="#F55069"
                    textAlign="center"
                    lineHeight="40px"
                    color="white"
                    fontSize="16px"
                    fontWeight="bold"
                  >
                    <ChakraText>This is a simulation for another user</ChakraText>
                  </Box>
                ) : null}
                {(exceededArray && exceededArray.length > 0) &&
                  <Box
                    width="full"
                    padding="20px"
                    bgColor="#F55069"
                    color="white">
                    <ChakraText>
                      {
                        exceededArray.length > 1 ?
                          <>There seem to be some issues with your account:</> :
                          <>There seems to be an issue with your account:</>
                      }
                    </ChakraText>
                    <UnorderedList>
                      {exceededArray.map((el) => (
                        <ListItem>{el}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                }
                <DiscountedPricingClosableInfoBar />
                <Flex>
                  {!isMediaLibraryWidget && !isPublicLink ? <NavBarLayout /> : null}
                  <Switch>
                    {isMediaLibraryWidget || isPublicLink ? (
                      <>
                        <Route exact path="/">
                          <Redirect to={routeSuffixToRedirectToForBaseUrl} />
                        </Route>

                        <Route path="/media-library">
                          <MediaLibraryRouter />
                        </Route>

                        <Route path="/media-collections">
                          <MediaCollectionRouter />
                        </Route>
                      </>
                    ) : (
                      <>
                        <Route exact path="/">
                          <HomePage />
                        </Route>

                        <Route path="/external-storage">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_INTEGRATION_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <ExternalStorageRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/url-endpoints">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_INTEGRATION_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <UrlEndpointsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/performance-alerts">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_PERFORMANCE_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PerformanceRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/developer">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_DEVELOPERS_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <DeveloperPage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/settings">
                          {user?.permissions.includes(
                            PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW,
                          ) ||
                            user?.permissions.includes(
                              PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
                            ) ? (
                            <CustomMetadataProvider>
                              <SettingsRouter />
                            </CustomMetadataProvider>
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path="/purge-cache">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_CLEAR_CACHE_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PurgeCachePage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/profile">
                          <UserProfilePage />
                        </Route>

                        <Route path="/referrals">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_REFERRAL_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <ReferralsPage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/support">
                          <SupportRouter />
                        </Route>

                        <Route
                          path="/billing"
                          render={() =>
                            user?.pricingPlan.isPaidPlan ? (
                              <PermissionGuard
                                permission={PermissionsEnum.SELF_BILLING_VIEW}
                              >
                                {(hasPermission: boolean) =>
                                  hasPermission ? (
                                    user.isAWSMarketplaceSubscription ? (
                                      <AWSManagedPage />
                                    ) : <BillingRouter />
                                  ) : (
                                    <Redirect to="/" />
                                  )
                                }
                              </PermissionGuard>
                            ) : (
                              <Redirect to="/upgrade-plan" />
                            )
                          }
                        />

                        <Route path="/analytics">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_ANALYTICS_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <AnalyticsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route
                          path="/user-management"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <UserManagementRouter />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route
                          path="/invoices"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_BILLING_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <InvoiceRouter />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />
                        <Route
                          path="/audit-logs"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <AuditLogsPage />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route
                          path="/dam-audit-logs"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <MediaAuditLogsPage />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route
                          path="/upgrade-plan"
                          render={() =>
                            <Redirect to="/change-plan" />
                          }
                        />

                        <Route path="/change-plan">
                          <PermissionGuard permission={PermissionsEnum.SELF_BILLING_MANAGE}>
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PlanChangePage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/media-library">
                          <PermissionGuard
                            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <MediaLibraryRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/media-collections">
                          <PermissionGuard
                            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <MediaCollectionRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/manage-accounts">
                          <AgencyManageAccountsRouter />
                        </Route>

                        <Route path="/agency-analytics">
                          <AgencyAnalyticsPage />
                        </Route>

                        <Route
                          path="/get-started"
                          render={() =>
                            user?.onboardingComplete ? (
                              <Redirect to="/" />
                            ) : (
                              <IDRegion />
                            )
                          }
                        />

                        <Route path="/labs">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <LabsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route exact path="/notifications">
                          <NotificationPage />
                        </Route>
                      </>
                    )}

                    <Route>
                      <NotFoundPage />
                    </Route>
                  </Switch>
                </Flex>
              </>
            </RouterBackStackProvider>
            <GlobalStyle />
          </Router>
        </FetchProvider>
      </Sentry.ErrorBoundary> : <></>
  )
}

export default Sentry.withProfiler(IKDashboardApp)
